
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTransactionHistory, resumeTransaction } from '@/services/subscription/transactionService';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Loader2, RefreshCw } from 'lucide-react';

interface TransactionHistoryProps {
  organizationId: string;
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({ organizationId }) => {
  const [processingTransaction, setProcessingTransaction] = useState<string | null>(null);
  
  const { data: transactions, isLoading, refetch } = useQuery({
    queryKey: ['transactions', organizationId],
    queryFn: () => getTransactionHistory(organizationId)
  });

  const handleResumeTransaction = async (transactionId: string) => {
    try {
      setProcessingTransaction(transactionId);
      const { url } = await resumeTransaction(transactionId);
      
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Impossible de reprendre le paiement");
      }
    } catch (error) {
      console.error('Erreur lors de la reprise du paiement:', error);
      toast.error("Erreur lors de la reprise du paiement");
    } finally {
      setProcessingTransaction(null);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Historique des transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-gray-200 rounded" />
            <div className="h-12 bg-gray-200 rounded" />
            <div className="h-12 bg-gray-200 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'pending':
        return 'bg-yellow-500';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Historique des transactions</CardTitle>
        <Button 
          variant="outline" 
          size="icon" 
          onClick={() => refetch()} 
          title="Rafraîchir"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {transactions?.map((transaction) => (
            <div 
              key={transaction.id} 
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div>
                <p className="font-medium">
                  {transaction.subscription_plans.display_name}
                </p>
                <p className="text-sm text-gray-500">
                  {format(new Date(transaction.created_at), 'PPP', { locale: fr })}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <p className="font-medium">{transaction.amount}€</p>
                <Badge className={getStatusColor(transaction.status)}>
                  {transaction.status}
                </Badge>
                {transaction.status === 'pending' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={processingTransaction === transaction.id}
                    onClick={() => handleResumeTransaction(transaction.id)}
                  >
                    {processingTransaction === transaction.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Traitement...
                      </>
                    ) : (
                      'Finaliser'
                    )}
                  </Button>
                )}
              </div>
            </div>
          ))}
          
          {(!transactions || transactions.length === 0) && (
            <p className="text-center text-gray-500 py-8">
              Aucune transaction à afficher
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
